import { useEffect } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useLoyaltyServiceOffer } from 'features/offer/hooks/use-loyalty-service-offer';
import { useStoreRequired } from 'features/store/hooks/use-store-required';

import { CardModal } from 'components/card-modal';
import { LocationEmptyState } from 'components/location-empty-state';
import { toaster } from 'components/toaster/toaster';
import { useExposeRenderReadyOffersRewardsExperiment } from 'experiments/use-render-ready-offers-rewards-experiment';
import IncentiveDetails from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useGetCmsAndLoyaltyOffers } from 'pages/loyalty/loyalty-offers/use-get-cms-and-loyalty-offers';
import { useGetIncentive } from 'pages/loyalty/loyalty-offers/use-get-incentive';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LoyaltyEngineId, LoyaltyOffer } from 'state/loyalty/types';
import { routes } from 'utils/routing';

export default function OfferDetailRoute() {
  const { noStoreSelected } = useStoreRequired();
  const { loyaltyEngineId } = useLocalSearchParams<{ loyaltyEngineId: LoyaltyEngineId }>();
  const {
    enabled: enableLoyaltyServiceOffers,
    eligibleForExposure,
  } = useEnableLoyaltyServiceOffers();

  useExposeRenderReadyOffersRewardsExperiment(eligibleForExposure && !noStoreSelected);

  if (noStoreSelected) {
    return (
      <CardModal alwaysShowHeader>
        <LocationEmptyState title={t('emptyOffersHeading')} message={t('emptyMenuMessage')} />
      </CardModal>
    );
  }

  if (enableLoyaltyServiceOffers) {
    return <ServiceOfferDetail loyaltyEngineId={loyaltyEngineId} />;
  }

  return <LegacyOfferDetail loyaltyEngineId={loyaltyEngineId} />;
}

function ServiceOfferDetail({ loyaltyEngineId }: { loyaltyEngineId: LoyaltyEngineId }) {
  const dispatch = useAppDispatch();
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const { selectedIncentive, isIncentivesLoading, refreshOffer } = useLoyaltyServiceOffer({
    loyaltyEngineId,
  });

  useEffect(() => {
    if (shouldRefetchOffers) {
      void refreshOffer();
      dispatch(actions.loyalty.setShouldRefetchOffers(false));
    }
  }, [dispatch, refreshOffer, shouldRefetchOffers]);

  return (
    <OfferDetailContent
      isIncentivesLoading={isIncentivesLoading}
      selectedIncentive={selectedIncentive}
    />
  );
}

function LegacyOfferDetail({ loyaltyEngineId }: { loyaltyEngineId: LoyaltyEngineId }) {
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const { refreshOffers } = useGetCmsAndLoyaltyOffers();
  const { selectedIncentive, isIncentivesLoading } = useGetIncentive({ loyaltyEngineId });

  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);

  return (
    <OfferDetailContent
      isIncentivesLoading={isIncentivesLoading}
      selectedIncentive={selectedIncentive}
      showUnavailableDefaultMessage={selectedIncentive?.isUnavailable}
    />
  );
}

function OfferDetailContent({
  isIncentivesLoading,
  selectedIncentive,
  showUnavailableDefaultMessage,
}: {
  isIncentivesLoading: boolean;
  selectedIncentive?: LoyaltyOffer & { isUnavailable?: boolean };
  showUnavailableDefaultMessage?: boolean;
}) {
  const router = useRouter();
  const incentiveExists = !!selectedIncentive;

  useEffect(() => {
    if (!isIncentivesLoading && !incentiveExists) {
      if (!router.canDismiss()) {
        return router.replace(routes.offers);
      }
      router.dismiss();
      toaster.negative(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
    }
  }, [incentiveExists, isIncentivesLoading, router]);

  // if the incentive doesn't exist above will dismiss
  if (isIncentivesLoading || !incentiveExists) {
    return (
      <CardModal alwaysShowHeader isLoading>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return (
    <IncentiveDetails
      engineIncentivesMap={{}}
      selectedIncentive={selectedIncentive}
      showUnavailableDefaultMessage={showUnavailableDefaultMessage}
    />
  );
}
